import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  // Demo mode - check if we have valid Supabase credentials
  const isDemoMode = !process.env.REACT_APP_SUPABASE_URL || 
                     process.env.REACT_APP_SUPABASE_URL === 'https://placeholder.supabase.co'

  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, skip Supabase auth
      setLoading(false)
      return
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [isDemoMode])

  const signIn = async (email: string, password: string) => {
    if (isDemoMode) {
      // Demo mode - allow login with hardcoded credentials
      let demoUser: User | null = null

      if (email === 'admin@cwm.com' && password === 'admin') {
        demoUser = {
          id: 'demo-admin',
          email: 'admin@cwm.com',
          role: 'authenticated'
        } as User
      }

      if (demoUser) {
        setUser(demoUser)
        setSession({ user: demoUser } as Session)
        return { error: null }
      } else {
        return { 
          error: { 
            message: 'Invalid credentials. Try: admin@cwm.com/admin' 
          } 
        }
      }
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    return { error }
  }

  const signOut = async () => {
    if (isDemoMode) {
      setUser(null)
      setSession(null)
      return
    }
    await supabase.auth.signOut()
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
